import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useAuth } from "./AuthContext";
import { useSocket } from "./SocketContext";
import type { BillingState } from "../OrgBillingPanel";

// Org-wide billing state for the logged-in user's organization. Loaded once
// after login and then kept live by the backend's `billing_state` socket
// event, which fires on every webhook, override, or sweep change. Techs get
// a reduced payload (no money fields); the banner only needs entitlement
// and system names.

interface BillingContextType {
  billing: BillingState | null;
  refresh: () => Promise<void>;
}

const BillingContext = createContext<BillingContextType>({ billing: null, refresh: async () => {} });

export const BillingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const auth = useAuth();
  const { socket } = useSocket();
  const [billing, setBilling] = useState<BillingState | null>(null);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch("/api/v1/billing/status", { credentials: "include" });
      if (!res.ok) return;
      setBilling(await res.json());
    } catch (_) {
      // Transient; the socket event or next mount will refresh it.
    }
  }, []);

  useEffect(() => {
    if (!auth.isAuthenticated) {
      setBilling(null);
      return;
    }
    refresh();
  }, [auth.isAuthenticated, refresh]);

  useEffect(() => {
    if (!socket || !auth.isAuthenticated) return;
    const onState = (state: BillingState) => setBilling(state);
    socket.on("billing_state", onState);
    return () => {
      socket.off("billing_state", onState);
    };
  }, [socket, auth.isAuthenticated]);

  return <BillingContext.Provider value={{ billing, refresh }}>{children}</BillingContext.Provider>;
};

export const useBilling = () => useContext(BillingContext);
